// Copyright (c) Docugami, Inc. All rights reserved.

import { DirectionalHint, ITooltipProps, TooltipDelay, TooltipHost } from '@fluentui/react';
import { DgFlexColumn } from 'components/common/DgCommonStyledComponents';
import { DgPill } from 'components/homepage/PreviewPill';
import { backgroundGray, homeGray, primaryTheme, themePinkLight } from 'components/shell/presentation/AppStyles';
import { IngestionWarnings } from 'networking/api';
import styled from 'styled-components';

const FileWarningPill = styled(DgPill)`
    display: flex;
    background-color: ${themePinkLight};
    color: ${primaryTheme.palette.black};
    line-height: 10px;
    padding: 5px;
`;

const ListContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 3px;
`;

export interface FileWarningInfo {
    label: string;
    description: string;
}

export const FileWarningIndicatorsList = ({ warningInfos }: { warningInfos: FileWarningInfo[] }): JSX.Element => {
    return (
        <ListContainer>
            { warningInfos.map(info => (
                <TooltipHost key={info.label} content={info.description} styles={{ root: { textAlign: 'center' } }} delay={TooltipDelay.zero} directionalHint={DirectionalHint.rightCenter} >
                    <FileWarningPill fontSize={10}>{info.label}</FileWarningPill>
                </TooltipHost>
            ))}
        </ListContainer>
    )
}

const TooltipContent = styled(DgFlexColumn)`
    gap: 10px;
`;

const ContentText = styled.span`
    color: ${homeGray};
    font-size: 12px;
    line-height: 15px;
`;

const TooltipSectionHeading = styled(ContentText)`
    font-weight: 700;
    font-size: 14px;
    line-height: 18px;
`;

const TooltipSection = styled(DgFlexColumn)`
    padding-bottom: 10px;
    border-bottom: 1px solid ${backgroundGray};
    gap: 3px;

    :last-of-type {
        border-bottom: none;
    }
`;

export const FileWarningIndicatorsCollapsed = ({ warningInfos }: { warningInfos: FileWarningInfo[] }): JSX.Element => {
    // Styling for the tooltip content (with sections), using each of the warning infos
    const tooltipProps: ITooltipProps = {
        onRenderContent: () => (
            <TooltipContent>
                {warningInfos.map((info) =>
                    <TooltipSection key={info.label}>
                        <TooltipSectionHeading>{info.label}</TooltipSectionHeading>
                        <ContentText>{info.description}</ContentText>
                    </TooltipSection>
                )}
            </TooltipContent>
        )
    };

    return (
        <TooltipHost key='file_precheck_issues' tooltipProps={tooltipProps} styles={{ root: { textAlign: 'center' } }} delay={TooltipDelay.zero} directionalHint={DirectionalHint.rightCenter} >
            <FileWarningPill fontSize={10.5}>File Issues</FileWarningPill>
        </TooltipHost>
    )
}


export const FileWarningsIndicator = ({ warnings }: { warnings?: IngestionWarnings }): JSX.Element => {
    const pageLimitExceededDescription = 'This document had more than the allowed number of pages at the time it was processed. Please contact Docugami if you would like this limit increased, or this document to be reprocessed.'
    const ingestionWarningToFileWarningInfo = new Map<IngestionWarnings, FileWarningInfo>([
        [IngestionWarnings.PageLimitExceeded, {label: 'Too Many Pages', description: pageLimitExceededDescription}],
        [IngestionWarnings.ChallengingLayout, {label: 'Best Effort', description: 'This document has a challenging layout and you may experience some issues.'}]
    ]);

    const warningInfos: FileWarningInfo[] = [];

    if (!!warnings) {
        ingestionWarningToFileWarningInfo.forEach((info, fileWarning) => {
            if (warnings & fileWarning) {
                warningInfos.push(info);
            }
        })
    }

    if (warningInfos.length <= 1) {
        return <FileWarningIndicatorsList warningInfos={warningInfos} />
    } else {
        return <FileWarningIndicatorsCollapsed warningInfos={warningInfos} />
    }
}
